import { useState } from "react";
import { DataTable } from "../components/DataTable";

// ─── Types ───────────────────────────────
type QuoteStatus = "New" | "In Review" | "Replied" | "Closed";

type QuoteRequest = {
  id: string;
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  budget: string;
  status: QuoteStatus;
  receivedAt: string;
  repliedAt: string;
  adminNote: string;
};

const STATUS_COLORS: Record<QuoteStatus, string> = {
  New: "text-bg-primary",
  "In Review": "text-bg-warning",
  Replied: "text-bg-success",
  Closed: "text-bg-secondary",
};

// ─── Delete Confirm Modal ─────────────────
function DeleteConfirmModal({
  itemLabel,
  onCancel,
  onConfirm,
}: {
  itemLabel: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <div
      className="ns-del-overlay"
      style={{ zIndex: 2100 }}
      onClick={e => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="ns-del-modal" role="dialog" aria-modal="true">
        <div className="ns-del-top">
          <div className="ns-del-icon">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6"/>
              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
              <path d="M10 11v6"/><path d="M14 11v6"/>
              <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
            </svg>
          </div>
          <div className="ns-del-title">Delete {itemLabel}?</div>
          <p className="ns-del-desc">
            This action cannot be undone. This {itemLabel.toLowerCase()} will be permanently removed.
          </p>
        </div>
        <div className="ns-del-hr" />
        <div className="ns-del-btns">
          <button type="button" className="ns-del-cancel" onClick={onCancel}>Cancel</button>
          <button type="button" className="ns-del-confirm" onClick={onConfirm}>Yes, Delete</button>
        </div>
      </div>
    </div>
  );
}

// ─── Detail Panel ─────────────────────────
function QuotePanel({
  quote,
  onClose,
  onUpdate,
  onDelete,
}: {
  quote: QuoteRequest;
  onClose: () => void;
  onUpdate: (q: QuoteRequest) => void;
  onDelete: (id: string) => void;
}) {
  const [form, setForm] = useState<QuoteRequest>({ ...quote });
  const [saved, setSaved] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  function set<K extends keyof QuoteRequest>(k: K, v: QuoteRequest[K]) {
    setForm(f => ({ ...f, [k]: v }));
  }

  function handleSave() {
    onUpdate(form);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  return (
    <>
      <div className="mty-panel-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="mty-panel">
          {/* Header */}
          <div className="mty-panel-header">
            <div className="d-flex align-items-center gap-2">
              <div className="mst-icon-box"><i className="bi bi-chat-quote"></i></div>
              <div>
                <div className="fw-bold mst-title">{quote.name}</div>
                <small className="mst-subtitle">{quote.email} · {quote.receivedAt}</small>
              </div>
            </div>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          {/* Body */}
          <div className="mty-panel-body">
            {saved && (
              <div className="alert alert-success d-flex align-items-center gap-2 py-2 mb-3 mst-alert">
                <i className="bi bi-check-circle-fill"></i> Saved!
              </div>
            )}

            <div className="mty-section-label"><i className="bi bi-person me-2"></i>Customer Info</div>
            <div className="row g-3 mb-4">
              <div className="col-md-6">
                <label className="form-label fw-semibold mst-label">Name</label>
                <input className="form-control mst-control" value={form.name} disabled />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold mst-label">Email</label>
                <input className="form-control mst-control" value={form.email} disabled />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold mst-label">Phone</label>
                <input className="form-control mst-control" value={form.phone} disabled />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold mst-label">Budget</label>
                <input className="form-control mst-control" value={form.budget} disabled />
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold mst-label">Subject</label>
                <input className="form-control mst-control" value={form.subject} disabled />
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold mst-label">Message</label>
                <textarea className="form-control mst-textarea" rows={4} value={form.message} disabled />
              </div>
            </div>

            <div className="mty-section-label"><i className="bi bi-gear me-2"></i>Admin Actions</div>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold mst-label">Status</label>
                <select className="form-select mst-select" value={form.status}
                  onChange={e => set("status", e.target.value as QuoteStatus)}>
                  <option value="New">New</option>
                  <option value="In Review">In Review</option>
                  <option value="Replied">Replied</option>
                  <option value="Closed">Closed</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold mst-label">Replied At</label>
                <input type="date" className="form-control mst-control" value={form.repliedAt}
                  onChange={e => set("repliedAt", e.target.value)} />
              </div>
              <div className="col-12">
                <label className="form-label fw-semibold mst-label">Admin Note <span className="mst-seo-opt">(internal)</span></label>
                <textarea className="form-control mst-textarea" rows={3}
                  placeholder="Add internal notes about this quote request..."
                  value={form.adminNote} onChange={e => set("adminNote", e.target.value)} />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mty-panel-footer">
            <button type="button" className="btn btn-danger btn-sm mst-btn-delete"
              onClick={() => setShowDeleteConfirm(true)}>
              <i className="bi bi-trash me-1"></i>Delete
            </button>
            <div className="d-flex gap-2">
              <button type="button" className="btn mst-btn-reset" onClick={onClose}>Cancel</button>
              <button type="button" className="btn btn-primary mst-btn-save" onClick={handleSave}>
                <i className="bi bi-check-circle me-2"></i>Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirm — rendered outside panel overlay so z-index stacks correctly */}
      {showDeleteConfirm && (
        <DeleteConfirmModal
          itemLabel="Quote Request"
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={() => { onDelete(quote.id); setShowDeleteConfirm(false); onClose(); }}
        />
      )}
    </>
  );
}

// ─── Dummy seed data ───────────────────────
const SEED: QuoteRequest[] = [
  {
    id: "q-1", name: "Priya Sharma", email: "priya@example.com", phone: "+91 98765 43210",
    subject: "Custom Wedding Ring", message: "I want a custom 22K gold wedding ring with diamond setting. Please share price and timeline.",
    budget: "₹50,000 – ₹1,00,000", status: "New", receivedAt: "14 May 2026", repliedAt: "", adminNote: "",
  },
  {
    id: "q-2", name: "Arjun Mehta", email: "arjun@example.com", phone: "+91 91234 56789",
    subject: "Bulk Order — Party Rings", message: "We need 20 matching party rings for a corporate event. Can you customise with logo engraving?",
    budget: "₹2,00,000+", status: "In Review", receivedAt: "13 May 2026", repliedAt: "", adminNote: "Checking with production team",
  },
  {
    id: "q-3", name: "Sunita Patel", email: "sunita@example.com", phone: "+91 87654 32109",
    subject: "Diamond Pendant", message: "Looking for a diamond pendant with solitaire setting, around 0.5 carat.",
    budget: "₹25,000 – ₹40,000", status: "Replied", receivedAt: "10 May 2026", repliedAt: "2026-05-11", adminNote: "Sent catalogue via email",
  },
];

// ─── Main Page ────────────────────────────
export function QuoteRequestsPage() {
  const [quotes, setQuotes] = useState<QuoteRequest[]>(SEED);
  const [selected, setSelected] = useState<QuoteRequest | null>(null);

  const counts = quotes.reduce((acc, q) => { acc[q.status] = (acc[q.status] ?? 0) + 1; return acc; }, {} as Record<string, number>);

  function handleUpdate(updated: QuoteRequest) {
    setQuotes(prev => prev.map(q => q.id === updated.id ? updated : q));
    setSelected(updated);
  }

  function handleDelete(id: string) {
    setQuotes(prev => prev.filter(q => q.id !== id));
    setSelected(null);
  }

  return (
    <div className="d-grid gap-4">

      {/* Stat Cards */}
      <div className="row g-3">
        {(["New","In Review","Replied","Closed"] as QuoteStatus[]).map(s => (
          <div className="col-6 col-md-3" key={s}>
            <div className="card border-0 shadow-sm mst-card h-100">
              <div className="card-body p-3 d-flex align-items-center gap-3">
                <div className="qr-stat-icon">
                  <i className={`bi ${s === "New" ? "bi-inbox" : s === "In Review" ? "bi-hourglass-split" : s === "Replied" ? "bi-check2-circle" : "bi-x-circle"}`}></i>
                </div>
                <div>
                  <div className="qr-stat-count">{counts[s] ?? 0}</div>
                  <div className="qr-stat-label">{s}</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <DataTable<Record<string, unknown>>
        title="Quote Requests"
        rows={quotes as unknown as Record<string, unknown>[]}
        rowKey={r => String(r.id)}
        searchPlaceholder="Search by name, email, subject..."
        exportFileName="quote-requests"
        onDeleteRow={(id) => handleDelete(id)}
        deleteItemLabel="Quote Request"
        columns={[
          {
            key: "name", label: "Customer", sortable: true,
            render: r => (
              <button type="button" className="btn btn-link p-0 text-start fw-semibold mst-title mty-row-link"
                onClick={() => setSelected(r as unknown as QuoteRequest)}>
                {String(r.name)}
                <i className="bi bi-box-arrow-up-right ms-1" style={{ fontSize: 11, opacity: 0.5 }}></i>
              </button>
            ),
          },
          { key: "email", label: "Email", render: r => <span className="mst-smoke" style={{ fontSize: 13 }}>{String(r.email)}</span> },
          { key: "subject", label: "Subject", sortable: true,
            render: r => <span style={{ maxWidth: 200, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{String(r.subject)}</span>
          },
          { key: "budget", label: "Budget", render: r => <span className="badge text-bg-secondary" style={{ fontWeight: 500 }}>{String(r.budget) || "—"}</span> },
          {
            key: "status", label: "Status",
            render: r => (
              <span className={`badge ${STATUS_COLORS[r.status as QuoteStatus]}`}>{String(r.status)}</span>
            ),
          },
          { key: "receivedAt", label: "Received", sortable: true },
          {
            key: "actions", label: "Actions", className: "text-end",
            render: r => (
              <div className="pg-actions">
                <button type="button" className="btn btn-sm btn-outline-primary mst-btn-status"
                  onClick={() => setSelected(r as unknown as QuoteRequest)} title="View / Edit">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                  </svg>
                </button>
                <button type="button" className="btn btn-sm btn-outline-danger btn-del">
                  <i className="bi bi-trash"></i>
                </button>
              </div>
            ),
          },
        ]}
      />

      {selected && (
        <QuotePanel
          quote={selected}
          onClose={() => setSelected(null)}
          onUpdate={handleUpdate}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}
